// A picture for a canvas that is read back (the export). One from another origin is fetched with CORS and drawn from a blob: URL,
// which never taints the canvas, and `no-store` keeps the browser from serving the copy the live board cached without CORS headers.
// The blob: URL is kept per source until `releasePictures`, so a dialog that redraws does not download a big picture again.
const copies = new Map<string, Promise<string>>();

const FETCH_MS = 30_000;

function sourceOf(url: string, signal?: AbortSignal): string | Promise<string> {
	const sameOrigin = new URL(url, location.href).origin === location.origin;
	if (sameOrigin || /^(blob|data):/.test(url)) return url;
	let copy = copies.get(url);
	if (!copy) {
		// A stalled host must not hold the export queue: the fetch ends at the timeout or when the caller aborts.
		const controller = new AbortController();
		const timer = setTimeout(() => controller.abort(new Error(`Timed out loading ${url}`)), FETCH_MS);
		signal?.addEventListener("abort", () => controller.abort(signal.reason), { once: true });
		const fresh: Promise<string> = fetch(url, { mode: "cors", credentials: "omit", cache: "no-store", signal: controller.signal })
			.then(async (response) => {
				if (!response.ok) throw new Error(`Could not load ${url}: ${response.status}`);
				return URL.createObjectURL(await response.blob());
			})
			.finally(() => clearTimeout(timer));
		copy = fresh;
		copies.set(url, fresh);
		fresh.catch(() => copies.get(url) === fresh && copies.delete(url));
	}
	return copy;
}

// The whole load, a download included, ends at the deadline or when the caller aborts, and leaves no handler on the image.
export function loadPicture(url: string, signal?: AbortSignal): Promise<HTMLImageElement> {
	if (signal?.aborted) return Promise.reject(signal.reason);
	return new Promise<HTMLImageElement>((resolve, reject) => {
		const image = new Image();
		image.decoding = "async";
		let done = false;
		const end = (settle: () => void) => {
			done = true;
			clearTimeout(timer);
			signal?.removeEventListener("abort", stop);
			image.onload = image.onerror = null;
			settle();
		};
		const fail = (error: unknown) => end(() => reject(error));
		const stop = () => fail(signal?.reason);
		const timer = setTimeout(() => fail(new Error(`Timed out loading ${url}`)), FETCH_MS);
		signal?.addEventListener("abort", stop, { once: true });
		image.onload = () => end(() => resolve(image));
		image.onerror = () => fail(new Error(`Could not load ${url}`));
		Promise.resolve(sourceOf(url, signal)).then((source) => {
			if (!done) image.src = source;
		}, fail);
	});
}

// Frees the blob: URLs; the pictures already drawn from them stay valid.
export function releasePictures() {
	for (const copy of copies.values())
		copy.then(
			(blobUrl) => URL.revokeObjectURL(blobUrl),
			() => {},
		);
	copies.clear();
}
